import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CreditCard, ExternalLink, CheckCircle2, Clock, Info, Check, X, Link2, Copy, Trash2, KeyRound, AlertTriangle, Gift,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, Spinner, ErrorMessage } from '../components/ui';
import { billingApi } from '../lib/api';
import type { BillingInfo, PlanId, PlanInfo } from '../lib/api';
import { useBilling } from '../lib/billing';
import { PLAN_NAMES, getPlanStatus } from '../lib/plans';

const PLAN_RANK: Record<string, number> = { free: 0, starter: 1, pro: 2, agency: 3 };
const PAID_STATUSES = ['active', 'trialing', 'past_due', 'completed'];

function formatDate(ms: number | null | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function UsageRow({ label, used, limit }: { label: string; used: number; limit: number | null | undefined }) {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : limit === 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const over = !unlimited && used >= (limit ?? 0);
  return (
    <div>
      <div className="flex items-center justify-between text-[13px]">
        <span className="font-semibold text-ink-2">{label}</span>
        <span className={`font-semibold ${over ? 'text-warning' : 'text-ink'}`}>
          {used} / {unlimited ? 'Unlimited' : limit}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-surface-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: unlimited ? '100%' : `${pct}%`,
            background: unlimited ? 'var(--accent-soft)' : over ? 'var(--warning)' : 'var(--accent)',
          }}
        />
      </div>
    </div>
  );
}

function FeatureRow({ label, enabled, detail }: { label: string; enabled: boolean; detail?: string }) {
  return (
    <div className="flex items-center gap-2 text-[13px]">
      {enabled ? <Check className="w-4 h-4 text-success flex-shrink-0" /> : <X className="w-4 h-4 text-ink-3 flex-shrink-0" />}
      <span className={enabled ? 'text-ink font-medium' : 'text-ink-3 font-medium'}>{label}</span>
      {detail && <span className="ml-auto text-ink-2 font-semibold">{detail}</span>}
    </div>
  );
}

function limitText(n: number | null | undefined): string {
  return n === null || n === undefined ? 'Unlimited' : String(n);
}

export default function Billing() {
  const { billing, loading, refresh, setBilling } = useBilling();
  const [searchParams, setSearchParams] = useSearchParams();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyPlan, setBusyPlan] = useState<PlanId | null>(null);
  const [linkCode, setLinkCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [removingLink, setRemovingLink] = useState<string | null>(null);
  const syncedRef = useRef(false);
  const returningFromCheckout = useRef(searchParams.get('checkout') === 'success' || searchParams.get('trial') === 'started');
  const [startingTrial, setStartingTrial] = useState(false);

  // Returning from Whop checkout: pull memberships now in case the webhook hasn't landed.
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success' || syncedRef.current) return;
    syncedRef.current = true;
    (async () => {
      const res = await billingApi.sync();
      if (res.success && res.data) {
        setBilling(res.data);
        setSuccess(`Thanks! You're on the ${PLAN_NAMES[res.data.plan] ?? res.data.plan} plan.`);
      } else {
        setSuccess('Payment received. Your plan will update in a moment — refresh if it has not changed.');
      }
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      setSearchParams(next, { replace: true });
    })();
  }, [searchParams, setSearchParams, setBilling]);

  // Returning from the free-trial claim on Whop: pull the membership in case the webhook hasn't landed.
  useEffect(() => {
    if (searchParams.get('trial') !== 'started' || syncedRef.current) return;
    syncedRef.current = true;
    (async () => {
      const res = await billingApi.sync();
      if (res.success && res.data) setBilling(res.data);
      setSuccess(res.success && res.data?.source === 'trial'
        ? 'Your 14-day Starter trial is active. Enjoy!'
        : 'Activating your trial — refresh in a moment if it has not started.');
      const next = new URLSearchParams(searchParams);
      next.delete('trial');
      setSearchParams(next, { replace: true });
    })();
  }, [searchParams, setSearchParams, setBilling]);

  // The provider loads once per session; refresh when the page opens so usage is current.
  // (Skipped when returning from checkout: the sync call returns fresh data itself.)
  useEffect(() => {
    if (!returningFromCheckout.current) refresh();
  }, [refresh]);

  const handleCheckout = async (plan: PlanId) => {
    setError(null);
    setBusyPlan(plan);
    try {
      const res = await billingApi.checkout(plan);
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError(res.message || 'Could not start checkout');
    } catch {
      setError('Could not start checkout');
    }
    setBusyPlan(null);
  };

  const handleStartTrial = async () => {
    setError(null);
    setStartingTrial(true);
    try {
      const res = await billingApi.startTrial();
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError(res.message || 'Could not start your trial');
    } catch {
      setError('Could not start your trial');
    }
    setStartingTrial(false);
  };

  const handleGenerateCode = async () => {
    setError(null);
    setGenerating(true);
    try {
      const res = await billingApi.createWhopLinkCode();
      if (res.success && res.data) setLinkCode(res.data);
      else setError(res.message || 'Could not generate a link code');
    } catch {
      setError('Could not generate a link code');
    } finally {
      setGenerating(false);
    }
  };

  const handleRemoveLink = async (accountId: string, title: string) => {
    if (!confirm(`Disconnect the Whop business "${title}"? Lead Sync for it will stop until it is linked again.`)) return;
    setError(null);
    setRemovingLink(accountId);
    try {
      const res = await billingApi.removeWhopLink(accountId);
      if (res.success) await refresh();
      else setError(res.message || 'Could not remove the link');
    } catch {
      setError('Could not remove the link');
    } finally {
      setRemovingLink(null);
    }
  };

  const copyCode = async () => {
    if (!linkCode) return;
    try {
      await navigator.clipboard.writeText(linkCode.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  if (!billing) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          {loading ? <Spinner /> : (
            <div className="text-center">
              <p className="text-ink-2 font-medium text-[14px]">Could not load billing details.</p>
              <Button variant="ghost" className="mt-3" onClick={refresh}>Try again</Button>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade">
        <PageHeader
          eyebrow="Account"
          title="Billing"
          description="Your plan, usage and subscription"
          actions={billing.canManageBilling && billing.subscription?.manageUrl ? (
            <Button variant="ghost" icon={ExternalLink} onClick={() => window.open(billing.subscription!.manageUrl!, '_blank', 'noopener')}>
              Manage billing
            </Button>
          ) : undefined}
        />

        {success && (
          <div className="mb-4 p-3 bg-success-soft border border-line rounded-[11px] flex items-center gap-2 text-success text-sm font-medium">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {success}
            <button onClick={() => setSuccess(null)} className="ml-auto text-ink-3 hover:text-ink"><X className="w-4 h-4" /></button>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
          </div>
        )}

        {!billing.canManageBilling && (
          <div className="mb-4 p-3 bg-surface-2 border border-line rounded-[11px] flex items-center gap-2 text-ink-2 text-sm font-medium">
            <Info className="w-4 h-4 flex-shrink-0 text-accent-ink" />
            Billing is managed by your agency account. Switch back to the agency account to change plans.
          </div>
        )}

        <TrialBanner billing={billing} />
        {billing.trialAvailable && <StartTrialCard onStart={handleStartTrial} busy={startingTrial} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <CurrentPlanCard billing={billing} />

          <Card padded className="lg:col-span-2">
            <h3 className="font-display text-base font-semibold text-ink">Usage</h3>
            <p className="text-[13px] text-ink-2 mt-1">What you've created against your plan's limits. Downgrades never delete data.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-5">
              <UsageRow label="Platform connections" used={billing.usage.connections} limit={billing.limits.connections} />
              <UsageRow label="Forms" used={billing.usage.forms} limit={billing.limits.forms} />
              <UsageRow label="Quizzes" used={billing.usage.quizzes} limit={billing.limits.quizzes !== undefined ? billing.limits.quizzes : billing.limits.forms} />
              <UsageRow label="Buttons" used={billing.usage.buttons} limit={billing.limits.buttons !== undefined ? billing.limits.buttons : billing.limits.forms} />
              <UsageRow label="Popups" used={billing.usage.popups} limit={billing.limits.popups !== undefined ? billing.limits.popups : billing.limits.forms} />
              <UsageRow label="Webhooks" used={billing.usage.webhooks} limit={billing.limits.webhooks !== undefined ? billing.limits.webhooks : billing.limits.forms} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-6 pt-5 border-t border-line">
              <FeatureRow label="Scheduled backups" enabled={billing.limits.scheduledBackups} />
              <FeatureRow label="Restore" enabled={billing.limits.restore} />
              <FeatureRow label="API keys & /api/v1" enabled={billing.limits.apiAccess} />
              <FeatureRow label="Whop Lead Sync app" enabled={billing.limits.whopApp} />
              <FeatureRow label="Team members" enabled detail={limitText(billing.limits.teamMembers)} />
              <FeatureRow label="Client workspaces" enabled={(billing.limits.workspaces ?? 1) !== 0} detail={billing.limits.workspaces ? limitText(billing.limits.workspaces) : undefined} />
            </div>
          </Card>
        </div>

        <h3 className="font-display text-lg font-bold text-ink mt-8 mb-3">Plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {billing.plans.filter((p) => p.id !== 'free').map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              billing={billing}
              busy={busyPlan === p.id}
              disabled={busyPlan !== null}
              onCheckout={() => handleCheckout(p.id)}
            />
          ))}
        </div>

        {/* Whop Lead Sync */}
        <Card padded className="mt-8">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-accent-ink" />
            <h3 className="font-display text-base font-semibold text-ink">Whop Lead Sync</h3>
            {!billing.limits.whopApp && <Badge tone="warning">Starter or above</Badge>}
          </div>
          <p className="text-[13px] text-ink-2 mt-1 max-w-3xl">
            The MyLeadSyncer app inside Whop syncs your Whop members into ClickFunnels. It needs a Starter plan or above
            (it works during your trial). If your plan lapses, syncs pause and resume once you're subscribed again — nothing is lost.
          </p>

          <div className="mt-5">
            <div className="ds-label">Connected Whop businesses</div>
            {billing.whopLinks.length === 0 ? (
              <p className="text-[13px] text-ink-3 font-medium">No Whop businesses linked yet.</p>
            ) : (
              <div className="divide-y divide-line border border-line rounded-[11px]">
                {billing.whopLinks.map((l) => (
                  <div key={l.accountId} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[13.5px] text-ink truncate">{l.title || l.accountId}</div>
                      <div className="font-mono text-[11.5px] text-ink-3 truncate">{l.accountId}</div>
                    </div>
                    <button
                      onClick={() => handleRemoveLink(l.accountId, l.title || l.accountId)}
                      disabled={removingLink === l.accountId}
                      title="Disconnect"
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-3 hover:text-danger hover:bg-danger-soft transition disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5">
            <div className="ds-label">Connect a Whop business</div>
            {linkCode ? (
              <div className="rounded-[11px] bg-surface-2 border border-line p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-bold text-[22px] tracking-[0.15em] text-ink">{linkCode.code}</span>
                  <button
                    onClick={copyCode}
                    className="p-2 bg-surface rounded-[10px] border border-line text-ink-3 hover:text-ink transition"
                    title="Copy code"
                  >
                    {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <span className="text-[12px] text-ink-3 font-medium inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    Expires {new Date(linkCode.expiresAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                <p className="text-[13px] text-ink-2 mt-2.5">
                  Open MyLeadSyncer in your Whop business dashboard and paste this code. It can be used once
                  {billing.isWorkspace ? ' and links the Whop business to this workspace' : ''}.
                </p>
                <Button variant="ghost" className="mt-3" onClick={handleGenerateCode} disabled={generating}>
                  {generating ? 'Generating…' : 'Generate a new code'}
                </Button>
              </div>
            ) : (
              <Button icon={KeyRound} onClick={handleGenerateCode} disabled={generating}>
                {generating ? 'Generating…' : 'Generate link code'}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

function StartTrialCard({ onStart, busy }: { onStart: () => void; busy: boolean }) {
  return (
    <div
      className="mb-5 relative overflow-hidden rounded-[15px] p-5 text-white"
      style={{ background: 'linear-gradient(150deg,var(--accent),var(--accent-hover))' }}
    >
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/15" />
      <div className="relative flex items-center gap-2 font-display font-bold text-[16px]">
        <Gift className="w-5 h-5" />
        Start your free 14-day Starter trial
      </div>
      <p className="relative mt-1 text-[13px] font-medium opacity-90 max-w-2xl">
        Activate it on Whop — it's free and no card is needed. You'll sign in or create a free Whop account, then come straight back here with every Starter feature unlocked.
      </p>
      <button
        onClick={onStart}
        disabled={busy}
        className="relative mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white text-accent-ink font-semibold text-[13px] hover:opacity-90 transition disabled:opacity-60"
      >
        {busy ? 'Opening Whop…' : 'Activate free trial'}
      </button>
    </div>
  );
}

function TrialBanner({ billing }: { billing: BillingInfo }) {
  if (billing.source !== 'trial') return null;
  const days = billing.trialDaysLeft ?? 0;
  return (
    <div
      className="mb-5 relative overflow-hidden rounded-[15px] p-5 text-white"
      style={{ background: 'linear-gradient(150deg,var(--accent),var(--accent-hover))' }}
    >
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/15" />
      <div className="relative flex items-center gap-2 font-display font-bold text-[16px]">
        <Clock className="w-5 h-5" />
        {days > 0 ? `${days} day${days === 1 ? '' : 's'} left in your Starter trial` : 'Your Starter trial ends today'}
      </div>
      <p className="relative mt-1 text-[13px] font-medium opacity-90 max-w-2xl">
        {billing.trialEndsAt ? `Trial ends ${formatDate(billing.trialEndsAt)}. ` : ''}
        Pick a plan below to keep everything running. Without one, your account moves to the Free plan — no data is deleted.
      </p>
    </div>
  );
}

function CurrentPlanCard({ billing }: { billing: BillingInfo }) {
  const status = getPlanStatus(billing);
  const sub = billing.subscription;
  return (
    <Card padded>
      <div className="flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-accent-ink" />
        <h3 className="font-display text-base font-semibold text-ink">Current plan</h3>
      </div>
      <div className="mt-4 font-display font-bold text-[30px] text-ink tracking-tight">
        {PLAN_NAMES[billing.plan] ?? billing.plan}
      </div>
      <div className="mt-2">
        <Badge tone={status.tone} dot>{status.label}</Badge>
      </div>
      <div className="mt-4 space-y-1.5 text-[13px] text-ink-2 font-medium">
        {sub?.renewsAt && status.key !== 'canceled' && <p>Renews {formatDate(sub.renewsAt)}</p>}
        {sub?.cancelAtPeriodEnd && sub.renewsAt && <p>Access ends {formatDate(sub.renewsAt)}</p>}
        {status.key === 'past_due' && <p className="text-danger">Your last payment failed. Update your payment method in Whop to keep your plan.</p>}
        {billing.plan === 'free' && <p>Free includes 1 of each resource. Upgrade to unlock more.</p>}
        {billing.isWorkspace && <p>This workspace uses your agency's plan.</p>}
      </div>
      {billing.canManageBilling && sub?.manageUrl && (
        <Button variant="ghost" icon={ExternalLink} className="mt-4 w-full justify-center" onClick={() => window.open(sub.manageUrl!, '_blank', 'noopener')}>
          Manage billing
        </Button>
      )}
    </Card>
  );
}

function PlanCard({
  plan, billing, busy, disabled, onCheckout,
}: {
  plan: PlanInfo;
  billing: BillingInfo;
  busy: boolean;
  disabled: boolean;
  onCheckout: () => void;
}) {
  const sub = billing.subscription;
  const hasPaidSub = !!sub && PAID_STATUSES.includes(sub.status);
  const isCurrent = billing.plan === plan.id;
  const isCurrentPaid = hasPaidSub && sub!.plan === plan.id;

  // One Whop subscription per account: changing plans means cancelling the current one
  // in Manage billing first, otherwise both would bill.
  const blockedBySub = hasPaidSub && !isCurrentPaid;
  let label = 'Upgrade';
  if (isCurrentPaid) label = 'Current plan';
  else if (blockedBySub) label = PLAN_RANK[plan.id] < PLAN_RANK[sub!.plan] ? 'Switch' : 'Upgrade';
  else if (isCurrent && billing.source === 'trial') label = `Subscribe to ${plan.name}`;

  return (
    <div
      className={`relative bg-surface rounded-[15px] p-5 shadow-card-sm flex flex-col border ${
        isCurrent ? 'border-accent ring-2 ring-[var(--accent-soft)]' : 'border-line'
      }`}
    >
      {isCurrent && (
        <span className="absolute -top-2.5 left-5">
          <Badge tone="accent">{billing.source === 'trial' ? 'Trial plan' : 'Current plan'}</Badge>
        </span>
      )}
      <div className="font-display font-bold text-[18px] text-ink">{plan.name}</div>
      {plan.tagline && <p className="text-[13px] text-ink-2 mt-1 font-medium">{plan.tagline}</p>}
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display font-bold text-[34px] text-ink tracking-tight">${plan.priceMonthly}</span>
        <span className="text-[13px] text-ink-3 font-semibold">/mo</span>
      </div>
      <ul className="mt-4 space-y-2 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-[13px] text-ink-2 font-medium">
            <Check className="w-4 h-4 text-success flex-shrink-0 mt-px" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {billing.canManageBilling && (
        <Button
          variant={isCurrentPaid ? 'ghost' : 'primary'}
          className="mt-5 w-full justify-center"
          disabled={isCurrentPaid || blockedBySub || disabled}
          onClick={onCheckout}
        >
          {busy ? 'Redirecting…' : label}
        </Button>
      )}
      {billing.canManageBilling && blockedBySub && (
        <p className="mt-2 text-[12px] text-ink-3 font-medium text-center">
          To change plans, cancel your current plan under Manage billing first.
        </p>
      )}
    </div>
  );
}
